import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { Message } from '../../types';
import type { ComponentPropsWithoutRef } from 'react';
import { CompassLogo } from './CompassLogo';
import {
  getGravatarUrl,
  preprocessMarkdownSnippet,
  isValidUrl,
} from '../../lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ChevronDown, Info, ExternalLink } from 'lucide-react';
import remarkGfm from 'remark-gfm';
import { AnimatePresence, motion } from 'framer-motion';

interface ChatMessageProps {
  message: Message;
  userEmail?: string;
}

export const ChatMessage = ({ message, userEmail }: ChatMessageProps) => {
  const isUser = message.role === 'user';
  const [userAvatarUrl, setUserAvatarUrl] = useState<string>('');
  const [expandedSourceIndex, setExpandedSourceIndex] = useState<number | null>(
    null,
  );

  // Normalize content - handle any escaped newlines from API
  const normalizedContent = message.content.replace(/\\n/g, '\n');

  // Generate Gravatar URL for user
  useEffect(() => {
    if (!isUser || !userEmail) {
      return;
    }

    let isMounted = true;

    getGravatarUrl(userEmail, 80).then((url) => {
      // Only update state if component is still mounted
      if (isMounted) {
        setUserAvatarUrl(url);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isUser, userEmail]);

  // Determine badge variant based on relevance score
  const getScoreBadgeVariant = (
    score: number,
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (score >= 0.8) return 'default';
    if (score >= 0.6) return 'secondary';
    return 'outline';
  };

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'justify-end' : ''}`}>
      {/* Compass Logo - Left side for assistant */}
      {!isUser && (
        <div className="shrink-0 pt-1">
          <CompassLogo size={32} />
        </div>
      )}

      {/* Message Content */}
      <div
        className={`flex flex-col gap-1 ${
          isUser ? 'items-end max-w-lg' : 'flex-1 items-start'
        }`}
      >
        {/* Role label */}
        <p className="text-muted-foreground text-[13px] font-medium leading-normal px-1">
          {isUser ? 'You' : 'Compass'}
        </p>

        {/* Message bubble */}
        {isUser ? (
          <Card className="max-w-lg py-3 px-4">
            <p className="text-base font-normal leading-normal">
              {normalizedContent}
            </p>
          </Card>
        ) : (
          <div className="w-full">
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-base prose-p:font-normal prose-p:leading-relaxed prose-p:my-2 first:prose-p:mt-0 last:prose-p:mb-0 prose-table:w-full prose-table:border-collapse prose-table:my-4 prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 px-1">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({
                    className,
                    children,
                    ...props
                  }: ComponentPropsWithoutRef<'code'> & {
                    inline?: boolean;
                  }) {
                    const match = /language-(\w+)/.exec(className || '');
                    // Check if this is a code block (has language or has newlines in content)
                    const content = String(children);
                    const hasNewlines = content.includes('\n');
                    const isBlock =
                      match || hasNewlines || props.inline === false;

                    return isBlock ? (
                      <SyntaxHighlighter
                        style={
                          oneDark as React.ComponentProps<
                            typeof SyntaxHighlighter
                          >['style']
                        }
                        language={match?.[1] || 'text'}
                        PreTag="div"
                      >
                        {content.replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {normalizedContent}
              </ReactMarkdown>
            </div>

            {/* Sources section */}
            {message.sources && message.sources.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium">
                  <Info className="h-4 w-4" />
                  <span>Sources ({message.sources.length})</span>
                </div>

                {message.sources.map((source, index) => {
                  const isExpanded = expandedSourceIndex === index;
                  const scorePercentage = Math.round(
                    source.relevance_score * 100,
                  );
                  const hasValidUrl =
                    source.document_url && isValidUrl(source.document_url);
                  const displayTitle =
                    source.title ||
                    `Source ${source.document_number ?? index + 1}`;

                  return (
                    <Card
                      key={source.document_url || `source-${index}`}
                      className="overflow-hidden py-0"
                    >
                      <div
                        className="flex items-center w-full px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() =>
                          setExpandedSourceIndex(isExpanded ? null : index)
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedSourceIndex(isExpanded ? null : index);
                          }
                        }}
                        aria-expanded={isExpanded}
                        aria-label={`${displayTitle}, ${scorePercentage}% relevance. Click to ${isExpanded ? 'collapse' : 'expand'}`}
                      >
                        {/* Document title - clickable link if URL available */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {hasValidUrl ? (
                            <a
                              href={source.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-primary hover:underline truncate inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                              title={displayTitle}
                            >
                              <span className="truncate">{displayTitle}</span>
                              <ExternalLink className="h-3 w-3 shrink-0" />
                            </a>
                          ) : (
                            <span
                              className="text-xs font-semibold truncate"
                              title={displayTitle}
                            >
                              {displayTitle}
                            </span>
                          )}
                          <Badge
                            variant={getScoreBadgeVariant(
                              source.relevance_score,
                            )}
                            className="shrink-0"
                          >
                            {scorePercentage}%
                          </Badge>
                        </div>
                        {/* Expand/collapse chevron indicator */}
                        <ChevronDown
                          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0 ml-2 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </div>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <CardContent className="px-3 pb-3 pt-0 space-y-2">
                              {/* Snippet rendered as Markdown with preprocessing */}
                              <div className="bg-muted rounded-md p-2 border">
                                <div className="prose prose-xs max-w-none dark:prose-invert prose-p:text-xs prose-p:my-1 prose-p:leading-relaxed prose-headings:text-xs prose-headings:my-1 prose-headings:font-semibold prose-li:text-xs prose-li:my-0 prose-ul:my-1 prose-ol:my-1 prose-code:text-xs prose-pre:my-1 prose-pre:p-2 prose-strong:font-semibold">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {preprocessMarkdownSnippet(source.snippet)}
                                  </ReactMarkdown>
                                </div>
                              </div>

                              {/* Metadata */}
                              {source.metadata &&
                                Object.keys(source.metadata).length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {Object.entries(source.metadata).map(
                                      ([key, value]) => {
                                        if (!value || value === '') return null;

                                        return (
                                          <Badge
                                            key={key}
                                            variant="secondary"
                                            title={`${key}: ${value}`}
                                          >
                                            <span className="font-semibold">
                                              {key}:
                                            </span>{' '}
                                            {String(value)}
                                          </Badge>
                                        );
                                      },
                                    )}
                                  </div>
                                )}
                            </CardContent>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Avatar - Right side */}
      {isUser && (
        <Avatar className="h-10 w-10 shrink-0">
          {userAvatarUrl && (
            <AvatarImage src={userAvatarUrl} alt="User avatar" />
          )}
          <AvatarFallback>
            {userEmail?.charAt(0).toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};
